/**
 * BlogTagPage — landing page for /blog/tag/<slug>.
 *
 * Mirror of BlogCategoryPage but filters by `post.tagSlugs[]` and
 * resolves the tag name from `client.listBlogTags`.
 *
 * @module @puffin-core/marketing-shell/blog/BlogTagPage
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BlogPostCard from './BlogPostCard.jsx';
import { DEFAULT_BLOG_UI_STRINGS } from './blogUiStrings.js';

/**
 * useBlogTagPosts — resolves the tag row + the posts filtered onto it, driving
 * the loading/ready/not-found/error machine. Extracted so <BlogTagPage> is left
 * as a pure render of the resolved taxonomy.
 *
 * @returns {{state: string, tag: object|null, posts: Array, error: Error|null}}
 */
function useBlogTagPosts({ client, tagSlug, locale, initialTag, initialPosts }) {
  const [state, setState] = useState(initialTag ? 'ready' : 'loading');
  const [tag, setTag] = useState(initialTag ?? null);
  const [posts, setPosts] = useState(initialPosts ?? []);
  const [error, setError] = useState(null);
  // BLOG-STD-001: server-rendered tag pages arrive seeded — skip the redundant
  // first fetch to avoid a hydration loading-flash.
  const seededRef = useRef(Boolean(initialTag));

  useEffect(() => {
    if (seededRef.current) {
      seededRef.current = false;
      return undefined;
    }
    let alive = true;
    setState('loading');
    setError(null);
    async function load() {
      // MLC-001 — taxonomy + posts in the active locale.
      const [tags, allPosts] = await Promise.all([
        client.listBlogTags({ locale }),
        client.listBlogPosts({ limit: 100, locale }),
      ]);
      if (!alive) return;
      const t = tags.find((x) => x.slug === tagSlug);
      if (!t) {
        setState('not_found');
        return;
      }
      const filtered = allPosts.filter(
        (p) => Array.isArray(p.tagSlugs) && p.tagSlugs.includes(tagSlug),
      );
      setTag(t);
      setPosts(filtered);
      setState('ready');
    }
    load().catch((err) => {
      if (!alive) return;
      setError(err);
      setState('error');
    });
    return () => {
      alive = false;
    };
  }, [client, tagSlug, locale]);

  return { state, tag, posts, error };
}

/**
 * One blog tag's page. Props are true to `BlogTagPage.propTypes` (PROTECTOR-SMS-001 U1b: the server render through
 * `blogPageElementFor` passes only what it has, so an optional prop must read as optional).
 *
 * @param {Object} props
 * @param {{listBlogTags: Function, listBlogPosts: Function}} props.client
 * @param {string} props.tagSlug
 * @param {Object} [props.initialTag]
 * @param {Array} [props.initialPosts]
 * @param {import('react').ReactNode} [props.loadingFallback=null]
 * @param {import('react').ReactNode} [props.notFoundFallback]
 * @param {Function} [props.errorFallback] - `({ error }) => node`
 * @param {string} [props.hrefPrefix='/blog']
 * @param {string} [props.readingMinutesLabel]
 * @param {string} [props.locale='en'] - the content locale
 * @param {Object} [props.strings] - the blog's chrome strings (`getBlogUiStrings`)
 * @returns {import('react').ReactNode} the page, or the fallback its state calls for
 */
export default function BlogTagPage({
  client,
  tagSlug,
  initialTag,
  initialPosts,
  loadingFallback = null,
  notFoundFallback,
  errorFallback,
  hrefPrefix = '/blog',
  readingMinutesLabel,
  // MLC-001: the active CONTENT locale (i18n short tag). Defaults to 'en'
  // (the model default) so EN behavior is byte-identical.
  locale = 'en',
  strings,
}) {
  // BLOG-STD-001: chrome strings from the ecosystem SSOT (see blogUiStrings.js);
  // absent `strings` → English defaults so existing callers are byte-identical.
  const s = strings ? { ...DEFAULT_BLOG_UI_STRINGS, ...strings } : DEFAULT_BLOG_UI_STRINGS;
  const resolvedReadingMinutesLabel = readingMinutesLabel ?? s.readingMinutesLabel;
  const { state, tag, posts, error } = useBlogTagPosts({
    client,
    tagSlug,
    locale,
    initialTag,
    initialPosts,
  });

  if (state === 'loading') return loadingFallback;
  if (state === 'not_found') return notFoundFallback || null;
  if (state === 'error') {
    if (typeof errorFallback === 'function') return errorFallback({ error });
    return (
      <section className="section-marketing" role="alert" aria-live="assertive">
        <h2 className="section-heading">{s.errorHeading}</h2>
        <p className="section-description">{s.errorBodyTag}</p>
      </section>
    );
  }

  return (
    <section className="section-marketing post-tag">
      <header className="section-heading-group">
        <p className="post-tag-eyebrow">{s.tagEyebrow}</p>
        <h1 className="section-heading">{tag.name}</h1>
        {posts.length === 0 ? <p className="section-description">{s.emptyTag}</p> : null}
      </header>
      {posts.length > 0 ? (
        <ul className="post-grid feature-grid">
          {posts.map((p) => (
            <li key={p.slug}>
              <BlogPostCard
                post={p}
                hrefPrefix={hrefPrefix}
                readingMinutesLabel={resolvedReadingMinutesLabel}
                locale={locale}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

BlogTagPage.propTypes = {
  client: PropTypes.shape({
    listBlogTags: PropTypes.func.isRequired,
    listBlogPosts: PropTypes.func.isRequired,
  }).isRequired,
  tagSlug: PropTypes.string.isRequired,
  initialTag: PropTypes.object,
  initialPosts: PropTypes.array,
  loadingFallback: PropTypes.node,
  notFoundFallback: PropTypes.node,
  errorFallback: PropTypes.func,
  hrefPrefix: PropTypes.string,
  readingMinutesLabel: PropTypes.string,
  locale: PropTypes.string,
  strings: PropTypes.object,
};
